"use client"

import type React from "react"

import { useEffect, useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { apiRequestWithAuth, getStoredToken, isTokenValid } from "@/lib/api-config"
import { useAuth } from "@/contexts/auth-context"
import { Button, Card, Input } from "animal-island-ui"
import { Users, CurrencyDollar as DollarSign, TrendUp as TrendingUp, UserCheck, Lock, Pulse as Activity, CaretDown as ChevronDown, SignOut as LogOut } from "@phosphor-icons/react"
import { useRouter } from "@/lib/navigation"
import NotLoggedIn from "@/components/not-logged-in"

const patterns = {
  "app-teal":   { border: "var(--leaf-mint)", bg: "var(--leaf-50)", color: "var(--leaf-800)", dot1: "rgba(130,213,187,0.18)", dot2: "rgba(170,235,210,0.12)" },
  "app-yellow":  { border: "var(--gold-400)", bg: "var(--gold-100)", color: "var(--gold-700)", dot1: "rgba(247,205,103,0.18)", dot2: "rgba(255,230,160,0.12)" },
  "warm-peach-pink": { border: "var(--ember-300)", bg: "var(--ember-50)", color: "var(--ember-600)", dot1: "rgba(225,140,111,0.18)", dot2: "rgba(255,185,160,0.12)" },
  "lime-green": { border: "var(--lime-500)", bg: "var(--gold-50)", color: "var(--gold-600)", dot1: "rgba(209,218,73,0.18)", dot2: "rgba(230,240,130,0.12)" },
  "app-orange": { border: "var(--ember-400)", bg: "var(--ember-50)", color: "var(--ember-600)", dot1: "rgba(229,146,102,0.18)", dot2: "rgba(255,190,150,0.12)" },
  "app-blue":   { border: "var(--sky-400)", bg: "var(--sky-50)", color: "var(--sky-700)", dot1: "rgba(136,157,240,0.18)", dot2: "rgba(180,195,255,0.12)" },
};

function patStyles(key: keyof typeof patterns) {
  const p = patterns[key];
  return {
    border: "1.5px solid " + p.border,
    borderRadius: 16,
    background: "radial-gradient(circle, " + p.dot1 + " 1.5px, transparent 1.5px), radial-gradient(circle, " + p.dot2 + " 1px, transparent 1px), " + p.bg,
    backgroundSize: "28px 28px, 14px 14px",
    backgroundPosition: "0 0, 7px 7px",
    color: p.color,
  };
}

export default function AdminDashboard() {
  const { token: contextToken, logout } = useAuth()
  const router = useRouter()
  const { toast } = useToast()
  const [statistics, setStatistics] = useState<any>(null)
  const [passwordForm, setPasswordForm] = useState({
    username: "",
    oldPassword: "",
    newPassword: "",
  })
  const [loading, setLoading] = useState(false)

  const getToken = () => {
    return contextToken || getStoredToken()
  }

  useEffect(() => {
    const token = getToken()
    if (token && isTokenValid(token)) {
      fetchStatistics()
    }
  }, [contextToken])

  const fetchStatistics = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({
        variant: "destructive",
        title: "认证失败",
        description: "请重新登录",
      })
      return
    }
    try {
      const result = await apiRequestWithAuth("/getStatistics", token, {
        method: "GET",
      })
      if (result.code === 200) {
        setStatistics(result.data)
      } else {
        toast({
          variant: "destructive",
          title: "获取统计数据失败",
          description: result.msg || "无法获取统计数据",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "网络错误",
        description: error instanceof Error ? error.message : "无法连接到服务器",
      })
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({
        variant: "destructive",
        title: "未授权",
        description: "请先登录",
      })
      return
    }

    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/changeAdminPassword", token, {
        method: "POST",
        body: JSON.stringify(passwordForm),
      })
      if (result.code === 200) {
        toast({
          variant: "success",
          title: "密码修改成功",
          description: "管理员密码已成功更新",
        })
        setPasswordForm({ username: "", oldPassword: "", newPassword: "" })
      } else {
        toast({
          variant: "destructive",
          title: "密码修改失败",
          description: result.msg || "请检查输入信息",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "密码修改失败",
        description: error instanceof Error ? error.message : "网络连接错误",
      })
    } finally {
      setLoading(false)
    }
  }

  const token = getToken()
  if (!token || !isTokenValid(token)) {
    return (
      <NotLoggedIn />
    )
  }

  return (
    <>
      <div className="admin-profile">
        <span className="admin-avatar">
          <img src="/avatars/default-user.png" alt="管理员头像" width={48} height={48} />
        </span>
        <h1>欢迎回来，老大</h1>
      </div>

      <div className="admin-statistics grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* 新用户数 - app-teal */}
        <Card style={{ padding: 20, ...patStyles("app-teal") }}>
          <div className="flex-between">
            <span className="fs-14-700">新用户数</span>
            <Users className="icon-muted" />
          </div>
          <div className="fs-28-800">{statistics?.newUserNum ?? "加载中..."}</div>
          <p className="fs-12-muted">本期新增用户</p>
        </Card>

        {/* 月收入 - app-yellow */}
        <Card style={{ padding: 20, ...patStyles("app-yellow") }}>
          <div className="flex-between">
            <span className="fs-14-700">月收入</span>
            <DollarSign className="icon-muted" />
          </div>
          <div className="fs-28-800">¥{statistics?.monthIncome?.toFixed(2) ?? "0.00"}</div>
          <p className="fs-12-muted">本月总收入</p>
        </Card>

        {/* 日收入 - warm-peach-pink */}
        <Card style={{ padding: 20, ...patStyles("warm-peach-pink") }}>
          <div className="flex-between">
            <span className="fs-14-700">日收入</span>
            <TrendingUp className="icon-muted" />
          </div>
          <div className="fs-28-800">¥{statistics?.dayIncome?.toFixed(2) ?? "0.00"}</div>
          <p className="fs-12-muted">今日收入</p>
        </Card>

        {/* 付费用户 - lime-green */}
        <Card style={{ padding: 20, ...patStyles("lime-green") }}>
          <div className="flex-between">
            <span className="fs-14-700">付费用户</span>
            <UserCheck className="icon-muted" />
          </div>
          <div className="fs-28-800">{statistics?.payedUserNum ?? "加载中..."}</div>
          <p className="fs-12-muted">总付费用户数</p>
        </Card>
      </div>

      <div className="admin-collapse-grid">
        {/* 修改管理员密码 - 折叠项 */}
        <details className="admin-collapse admin-collapse--password">
          <summary>
            <Lock className="admin-collapse-icon" aria-hidden="true" />
            <span className="admin-collapse-title">修改管理员密码</span>
            <ChevronDown className="admin-collapse-chevron" aria-hidden="true" />
          </summary>
          <form onSubmit={handleChangePassword} className="admin-collapse-body">
            <div className="flex-col-gap6">
              <label htmlFor="username" className="fs-14-600">用户名</label>
              <Input
                id="username"
                type="text"
                value={passwordForm.username}
                onChange={(e) => setPasswordForm({ ...passwordForm, username: e.target.value })}
                placeholder="请输入管理员用户名"
                size="small"
                shadow
                required
              />
            </div>
            <div className="flex-col-gap6">
              <label htmlFor="oldPassword" className="fs-14-600">当前密码</label>
              <Input
                id="oldPassword"
                type="password"
                value={passwordForm.oldPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                placeholder="请输入当前密码"
                size="small"
                shadow
                required
              />
            </div>
            <div className="flex-col-gap6">
              <label htmlFor="newPassword" className="fs-14-600">新密码</label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="请输入新密码"
                size="small"
                shadow
                required
              />
            </div>
            <Button type="default" block onClick={handleChangePassword} loading={loading} htmlType="submit">
              {loading ? "修改中..." : "修改密码"}
            </Button>
          </form>
        </details>

        {/* 系统状态 - 折叠项，仅保留在线用户 */}
        <details className="admin-collapse admin-collapse--status">
          <summary>
            <Activity className="admin-collapse-icon" aria-hidden="true" />
            <span className="admin-collapse-title">系统状态</span>
            <strong className="admin-collapse-meta">在线 {statistics?.payedUserNum ?? 0} 人</strong>
            <ChevronDown className="admin-collapse-chevron" aria-hidden="true" />
          </summary>
          <div className="admin-collapse-body">
            <div className="admin-status-online">
              <span className="admin-status-num">{statistics?.payedUserNum ?? 0}</span>
              <span className="admin-status-label">当前在线用户</span>
            </div>
          </div>
        </details>
      </div>
      <button className="admin-home-logout" type="button" onClick={() => { logout(); router.push("/") }}>
        <LogOut size={20} aria-hidden="true" />退出登录
      </button>
    </>
  )
}
